import { getCurrentInstance, onMounted, ref, type Ref } from 'vue'

import ReimbursementRequestService from '@/services/clubs/reimbursementRequestService'
import { handleRequest } from '@/utils/handleRequest';

interface CacheEntry {
  reimbursementRequest: Ref<any>
  loaded: boolean
  inFlight: Promise<any> | null
}

// Shared per rmbId so the show/edit route guards' fetch is reused by the
// mounted view instead of refetching the same record.
const cache = new Map<string, CacheEntry>()

function entryFor(rmbId: string): CacheEntry {
  let entry = cache.get(rmbId)
  if (!entry) {
    entry = { reimbursementRequest: ref(null), loaded: false, inFlight: null }
    cache.set(rmbId, entry)
  }
  return entry
}

// Route-guard policy for a single reimbursement request. Distinct from
// useReimbursementRequestPolicy (which reads `policies` off an already-loaded
// record) because the guard runs before the view exists and needs to fetch
// the record itself.
//
// Also covers the draft-resume route: restore_draft authorizes via
// DraftPolicy#restore? (record.user_id == user.id), so a 403 there means the
// draftId in the URL belongs to someone else.
//
// And the "start a new request" route via canCreate(clubId): club_eligible
// calls `authorize ReimbursementRequest.new(club: club), :create?` before it
// ever looks at club_code, so a 403 means the user isn't a club member for
// the current/previous term — a navigation-blocking permission failure,
// distinct from the club_code business rule (surfaced in New.vue as a
// non-blocking banner). Takes clubId as a param rather than the factory arg
// since it's a different id-space than rmbId (which keys the show/edit/draft
// cache above).
export function useReimbursementRequestRoutePolicy(rmbId: string | number) {

  const entry = entryFor(String(rmbId))
  const reimbursementRequest = entry.reimbursementRequest

  const fetchReimbursementRequest = async ({ force = true } = {}) => {
    if (!force && entry.loaded) return { success: true, data: reimbursementRequest.value }
    if (entry.inFlight) return await entry.inFlight

    entry.inFlight = handleRequest(
      () => ReimbursementRequestService.getReimbursementRequest(rmbId),
      {
        onSuccess: (response: any) => {
          reimbursementRequest.value = response.data
          entry.loaded = true
        }
      }
    )

    try {
      return await entry.inFlight
    } finally {
      entry.inFlight = null
    }
  }

  const canShow = () => !!reimbursementRequest.value?.policies?.show
  const canUpdate = () => !!reimbursementRequest.value?.policies?.update

  const canRestoreDraft = async () => {
    const result: any = await handleRequest(() => ReimbursementRequestService.restoreDraft(rmbId))
    return result?.success !== false
  }

  const canCreate = async (clubId: string | number) => {
    const result: any = await handleRequest(() => ReimbursementRequestService.checkClubEligible(clubId))
    return result?.success !== false
  }

  // Auto-fetch only when used inside a component; route guards call
  // fetchReimbursementRequest() explicitly and have no instance to mount against.
  if (getCurrentInstance()) {
    onMounted(async () => await fetchReimbursementRequest({ force: false }))
  }

  return {
    reimbursementRequest,
    fetchReimbursementRequest,
    canShow,
    canUpdate,
    canRestoreDraft,
    canCreate
  }
}
